import { useEffect, useRef, useState } from 'react'
import { LEVEL_VISUALIZER_DELAY } from '@/features/twilio/TwilioConstants'

export function useMicrophoneLevels(context: AudioContext, deviceId: string) {
  const [levels, setLevels] = useState(0)
  const setInputVolRef = useRef<((vol: number) => void) | null>(null)
  useEffect(() => {
    setInputVolRef.current = (vol: number) => setLevels(vol)
  }, [setLevels])

  useEffect(() => {
    const promise = (async () => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId },
      })

      if (context.state === 'suspended') await context.resume()

      const analyser = context.createAnalyser()
      analyser.smoothingTimeConstant = 0.4
      analyser.fftSize = 64
      const microphone = context.createMediaStreamSource(stream)
      microphone.connect(analyser)

      const fDataBytes = new Uint8Array(analyser.frequencyBinCount)

      const volumeEvent = () => {
        analyser.getByteFrequencyData(fDataBytes)
        const volume =
          fDataBytes.reduce((sum, val) => sum + val, 0) / fDataBytes.length
        const clipped = Math.min(255, volume)
        setInputVolRef.current?.(clipped / 255.0)
      }

      const interval = window.setInterval(volumeEvent, LEVEL_VISUALIZER_DELAY)

      return {
        stream,
        nodes: [analyser, microphone],
        interval,
      }
    })()

    return () => {
      promise.then((cleaner) => {
        if (cleaner) {
          cleaner.stream.getAudioTracks().forEach((track) => track.stop())
          cleaner.nodes[0].disconnect()
          cleaner.nodes[1].disconnect()

          window.clearInterval(cleaner.interval)
        }
      })
    }
  }, [deviceId])

  return {
    levels,
  }
}
